import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { Calendar, ChevronDown, ChevronUp, Search } from "lucide-react";
import { TailSpin } from "react-loader-spinner";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import axios from "axios";
import { MdFilterListOff } from "react-icons/md";
import { Badge } from "./ui/badge";
import { SearchableSelect } from "./ui/searchable-select";
import { cn } from "../lib/utils";
import {
  ActionArchiveIcon,
  ActionDeleteIcon,
  ActionEditIcon,
  ActionGenerateIcon,
  ActionOmitIcon,
  ActionUnarchiveIcon,
  ArchivedSectionIcon,
  DashIconWrap,
  DollarHandIcon,
  DollarSignIcon,
  InvoiceDocIcon,
  InvoiceEditIcon,
  PeopleIcon,
  PendingSectionIcon,
  PrintedSectionIcon,
  ProgressChartIcon,
} from "./DashboardIcons";
import { PERMISSIONS } from "../utils/rbac";
import { formatPayPeriodRange } from "../utils/displayDate";

const TH_CLASS =
  "p-4 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wide bg-[#F9FAFB] border-b border-[#E5E7EB]";
const TD_CLASS =
  "p-4 text-sm font-normal text-[#6B7280] border-b border-[#E5E7EB] align-middle";
const TD_NUM_CLASS =
  "p-4 text-sm font-medium text-[#111827] border-b border-[#E5E7EB] align-middle";
const TR_CLASS =
  "border-b border-[#E5E7EB] transition-colors hover:bg-gray-50 last:border-b-0";
const PAGER_BTN_CLASS =
  "h-10 min-w-[40px] px-3 flex items-center justify-center bg-white border border-[#E5E7EB] rounded-md text-[#6B7280] cursor-pointer transition-all text-sm font-medium hover:bg-[#F9FAFB] hover:border-[#166534] hover:text-[#166534] disabled:opacity-50 disabled:cursor-not-allowed";
const FILTER_SELECT_CLASS =
  "w-full lg:min-w-[180px] bg-white border-[#E5E7EB] text-[#1F2937]";
const apiUrl = process.env.REACT_APP_API_URL;

const WHITE = "#FFFFFF";

function formatPeriodMid(start, end) {
  return formatPayPeriodRange(start, end);
}

function formatLastUpdated(value) {
  if (!value) return "—";
  const d = moment(value);
  if (!d.isValid()) return "—";
  if (d.isSame(moment(), "day")) return `Today, ${d.format("h:mm A")}`;
  if (d.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return d.format("MMM DD, YYYY");
}

function employeeInitial(name) {
  return String(name || "?").charAt(0).toUpperCase();
}

function displayName(name) {
  const value = String(name || "");
  if (!value) return "—";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function payslipAmount(raw, formatInvoiceAdditions, formatValue) {
  if (raw === undefined || raw === null || raw === "") {
    return "-";
  }
  const numericValue = Number(raw);
  if (!Number.isNaN(numericValue)) {
    if (formatValue && numericValue === 0) {
      return "-";
    }
    return `$${numericValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  let text = formatInvoiceAdditions(raw);
  if (formatValue) {
    text = formatValue(text);
  }
  if (text === "-" || text === "—" || text === "0.00") {
    return formatValue ? "-" : "$0.00";
  }
  return text.startsWith("$") ? text : `$${text}`;
}

function ActionBtn({ title, className = "", onClick, children }) {
  return (
    <Tippy content={title}>
      <button
        type="button"
        className={`dash-action-btn ${className}`.trim()}
        title={title}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
      >
        {children}
      </button>
    </Tippy>
  );
}

function EmployeeCell({ name, email, image }) {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      {image ? (
        <img
          src={image}
          alt=""
          className="w-8 h-8 min-w-8 rounded-full object-cover"
        />
      ) : (
        <span className="w-8 h-8 min-w-8 rounded-full bg-[#166534] text-white inline-flex items-center justify-center text-xs font-medium">
          {employeeInitial(name)}
        </span>
      )}
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium text-[#111827] truncate">
          {displayName(name)}
        </span>
        {email ? (
          <span className="text-xs font-normal text-[#6B7280] truncate">
            {email}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function CountPill({ count, active }) {
  return (
    <span className={`dash-count-pill${active ? " is-active" : ""}`}>
      {count}
    </span>
  );
}

function HeadingCountPill({ count }) {
  return (
    <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-md bg-[#DCFCE7] text-[#166534] text-[11px] font-medium">
      {count}
    </span>
  );
}

function getPageNumbers(currentPage, totalPages) {
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i += 1) pages.push(i);
    return pages;
  }
  pages.push(1);
  if (currentPage <= 3) {
    pages.push(2, 3, "dots", totalPages);
  } else if (currentPage >= totalPages - 2) {
    pages.push("dots", totalPages - 2, totalPages - 1, totalPages);
  } else {
    pages.push(
      "dots",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "dots",
      totalPages
    );
  }
  return pages;
}

function getDateRangeWindow(dateRange, periodStart, periodEnd) {
  if (dateRange === "All Time") {
    return { start: null, end: null };
  }
  if (dateRange === "Current Period") {
    return {
      start: periodStart ? moment(periodStart).startOf("day") : null,
      end: periodEnd ? moment(periodEnd).endOf("day") : null,
    };
  }
  const end = moment().endOf("day");
  let start = null;
  if (dateRange === "Last 7 Days") {
    start = moment().subtract(7, "days").startOf("day");
  } else if (dateRange === "Last 30 Days") {
    start = moment().subtract(30, "days").startOf("day");
  } else if (dateRange === "Last 90 Days") {
    start = moment().subtract(90, "days").startOf("day");
  } else if (dateRange === "This Month") {
    return { start: moment().startOf("month"), end: moment().endOf("month") };
  } else if (dateRange === "Last Month") {
    const prev = moment().subtract(1, "month");
    return { start: prev.clone().startOf("month"), end: prev.clone().endOf("month") };
  }
  return { start, end };
}

function TablePager({ page, pageSize, total, onPageChange }) {
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const pageNumbers = getPageNumbers(page, totalPages);

  return (
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 px-4 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] text-sm font-normal text-[#6B7280]">
      <div>
        Showing {start} to {end} of {total} results
      </div>
      {totalPages > 1 ? (
        <div className="flex gap-2 items-center flex-wrap">
          <button
            type="button"
            className={PAGER_BTN_CLASS}
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page === 1}
          >
            Prev
          </button>
          {pageNumbers.map((item, index) =>
            item === "dots" ? (
              <span
                key={`dots-${index}`}
                className="h-10 w-10 flex items-center justify-center text-[#6B7280]"
              >
                ...
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={cn(
                  PAGER_BTN_CLASS,
                  item === page &&
                    "bg-[#166534] border-[#166534] text-white hover:bg-[#14532d] hover:text-white hover:border-[#14532d]"
                )}
                onClick={() => onPageChange(item)}
                aria-current={item === page ? "page" : undefined}
              >
                {item}
              </button>
            )
          )}
          <button
            type="button"
            className={PAGER_BTN_CLASS}
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

function SectionCard({
  icon,
  title,
  count,
  extra,
  defaultOpen = true,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-[#E5E7EB] mb-4 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3.5 bg-[#F9FAFB] border-b border-[#E5E7EB]">
        <button
          type="button"
          className="inline-flex items-center gap-2.5 bg-transparent border-none p-0 cursor-pointer text-[#111827]"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="inline-flex items-center justify-center shrink-0">
            {icon}
          </span>
          <span className="text-[15px] font-medium">{title}</span>
          <HeadingCountPill count={count} />
        </button>
        <span className="inline-flex items-center gap-2.5">
          {extra}
          <button
            type="button"
            className="inline-flex items-center justify-center bg-transparent border-none p-0 cursor-pointer text-[#6B7280]"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </span>
      </div>
      {open ? children : null}
    </div>
  );
}

function DashboardPeriodView({
  startDate,
  endDate,
  currentActive,
  mainTab,
  childTab,
  onMainTab,
  onChildTab,
  onBack,
  totalUsers,
  pendingCount,
  generatedCount,
  percentage,
  invoiceStats,
  periodInvoices,
  rangeId,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  users,
  userListLoading,
  currentPage,
  pageSize,
  pendingTotal,
  onPendingPage,
  onZeroSubmission,
  printedList,
  printedListLoading,
  currentPagePrinted,
  pageSizePrinted,
  totalPrintedUsers,
  onPrintedPage,
  archivedList,
  archivedListLoading,
  currentPageArchived,
  pageSizeArchived,
  totalArchivedUsers,
  onArchivedPage,
  formatInvoiceAdditions,
  formatValue,
  formatDashMoney,
  formatPeriodLong,
  archiveInvoice,
  handleDeleteInvoice,
  handleGenerateAllPdfs,
  onEditYtd,
  canArchiveInvoice,
  canEditInvoice,
  canDeleteInvoice,
  canManageYtd,
  canGeneratePdf,
  canCreatePayslip,
  canCreateInvoice,
  deniedActionClass,
  guardAction,
}) {
  const navigate = useNavigate();
  const isPayslips = mainTab !== "invoices";
  const roundedPct = Math.round(Number(percentage) || 0);
  const archivedTotal = Array.isArray(totalArchivedUsers)
    ? totalArchivedUsers.length
    : Number(totalArchivedUsers || 0);
  const pendingTotalSafe = Number(pendingTotal || pendingCount || 0);
  const printedTotalSafe = Number(totalPrintedUsers || 0);
  const allPayslipCount =
    pendingTotalSafe + printedTotalSafe + archivedTotal;
  const stats = invoiceStats || {
    sentTotal: 0,
    sentCount: 0,
    draftTotal: 0,
    draftCount: 0,
  };

  const [invoiceProperty, setInvoiceProperty] = useState("All Properties");
  const [invoiceStatus, setInvoiceStatus] = useState("All");
  const [invoiceDateRange, setInvoiceDateRange] = useState("Current Period");
  const [invoicePage, setInvoicePage] = useState(1);
  const invoicePageSize = 10;
  const [invoiceRows, setInvoiceRows] = useState([]);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [propertyOptions, setPropertyOptions] = useState([
    { value: "All Properties", label: "All Properties" },
  ]);

  useEffect(() => {
    let cancelled = false;
    const fetchProperties = async () => {
      if (!apiUrl) return;
      try {
        const res = await axios.get(`${apiUrl}/getPropertiesAndDeductions`);
        if (cancelled) return;
        const raw = Array.isArray(res?.data?.properties)
          ? res.data.properties
          : [];
        const opts = raw
          .map((p) => {
            const name = p?.NAME ?? p?.name;
            if (!name) return null;
            const v = String(name).trim();
            if (!v) return null;
            return { value: v, label: v };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label));
        setPropertyOptions([
          { value: "All Properties", label: "All Properties" },
          ...opts,
        ]);
      } catch (e) {
        if (!cancelled) {
          setPropertyOptions([
            { value: "All Properties", label: "All Properties" },
          ]);
        }
      }
    };
    fetchProperties();
    return () => {
      cancelled = true;
    };
  }, []);

  const statusOptions = useMemo(
    () => [
      { value: "All", label: "All Statuses" },
      { value: "SENT", label: "SENT" },
      { value: "PAID", label: "PAID" },
      { value: "DRAFT", label: "DRAFT" },
    ],
    []
  );

  const dateRangeOptions = useMemo(
    () => [
      {
        value: "Current Period",
        label: formatPeriodLong
          ? formatPeriodLong(startDate, endDate)
          : "Current Period",
      },
      { value: "All Time", label: "All Time" },
      { value: "Last 7 Days", label: "Last 7 Days" },
      { value: "Last 30 Days", label: "Last 30 Days" },
      { value: "Last 90 Days", label: "Last 90 Days" },
      { value: "This Month", label: "This Month" },
      { value: "Last Month", label: "Last Month" },
    ],
    [startDate, endDate, formatPeriodLong]
  );

  useEffect(() => {
    setInvoicePage(1);
  }, [searchValue, invoiceProperty, invoiceStatus, invoiceDateRange]);

  useEffect(() => {
    if (isPayslips) return undefined;
    let cancelled = false;
    const fetchFilteredInvoices = async () => {
      if (!apiUrl) return;
      setInvoiceLoading(true);
      try {
        const params = {
          page: invoicePage,
          pageSize: invoicePageSize,
          status: invoiceStatus === "All" ? "ALL" : invoiceStatus,
          property: invoiceProperty,
          q: searchValue || "",
        };
        const window = getDateRangeWindow(
          invoiceDateRange,
          startDate,
          endDate
        );
        if (window.start) {
          params.dateFrom = window.start.format("YYYY-MM-DD");
        }
        if (window.end) {
          params.dateTo = window.end.format("YYYY-MM-DD");
        }
        const res = await axios.get(`${apiUrl}/api/invoices`, { params });
        if (cancelled) return;
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setInvoiceRows(rows);
        setInvoiceTotal(Number(res?.data?.totalCount || rows.length || 0));
      } catch (e) {
        if (!cancelled) {
          setInvoiceRows([]);
          setInvoiceTotal(0);
        }
      } finally {
        if (!cancelled) setInvoiceLoading(false);
      }
    };
    fetchFilteredInvoices();
    return () => {
      cancelled = true;
    };
  }, [
    isPayslips,
    invoicePage,
    invoicePageSize,
    invoiceStatus,
    invoiceProperty,
    searchValue,
    invoiceDateRange,
    startDate,
    endDate,
  ]);

  const hasInvoiceFilters =
    String(searchValue || "").trim().length > 0 ||
    invoiceDateRange !== "Current Period" ||
    invoiceProperty !== "All Properties" ||
    invoiceStatus !== "All";

  const getStatusVariant = (status) => {
    switch (String(status || "").toUpperCase()) {
      case "SENT":
        return "sent";
      case "PAID":
        return "paid";
      case "APPROVED":
        return "approved";
      case "DRAFT":
        return "draft";
      case "DELETED":
        return "deleted";
      default:
        return "draft";
    }
  };

  const payslipChildTabs = [
    { id: "all", label: "All", count: allPayslipCount },
    { id: "pending", label: "Pending Payslips", count: pendingTotalSafe },
    { id: "printed", label: "To Be Printed", count: printedTotalSafe },
    { id: "archived", label: "Archived List", count: archivedTotal },
  ];
  const childTabs = payslipChildTabs;
  const showPending = isPayslips && (childTab === "all" || childTab === "pending");
  const showPrinted = isPayslips && (childTab === "all" || childTab === "printed");
  const showArchived =
    isPayslips && (childTab === "all" || childTab === "archived");

  const periodQuery = (() => {
    const params = new URLSearchParams();
    if (rangeId) params.set("rangeId", String(rangeId));
    if (startDate) params.set("startDate", String(startDate));
    if (endDate) params.set("endDate", String(endDate));
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  })();

  const editPayslipPath = (invoiceId) => {
    const params = new URLSearchParams({ id: String(invoiceId) });
    if (rangeId) params.set("rangeId", String(rangeId));
    if (startDate) params.set("startDate", String(startDate));
    if (endDate) params.set("endDate", String(endDate));
    return `/editInvoice?${params.toString()}`;
  };

  const goGenerate = (userId) => {
    guardAction(PERMISSIONS.CREATE_PAYSLIP, "generate invoice", () => {
      navigate(`/generateInvoice${periodQuery}`, {
        state: userId,
      });
    });
  };

  const openPayslipRow = (invoice) => {
    if (!invoice?.id) return;
    navigate(editPayslipPath(invoice.id), { state: invoice.id });
  };

  const openPendingRow = (user) => {
    if (!user?.id) return;
    goGenerate(user.id);
  };

  const openCompanyInvoiceRow = (invoice) => {
    if (!invoice?.id) return;
    const status = String(invoice.status || "").toUpperCase();
    if (status === "DRAFT") {
      navigate(
        `/generateInvoiceNew?invoiceId=${encodeURIComponent(String(invoice.id))}`
      );
      return;
    }
    navigate(
      `/generateInvoiceNew?invoiceId=${encodeURIComponent(
        String(invoice.id)
      )}&forceStep=3`
    );
  };

  const periodLabel =
    currentActive === "active" ? "Current Pay Period" : "Previous Pay Period";

  return (
    <div className="dash-period-view">
      <div className="dash-breadcrumb">
        <button type="button" onClick={onBack}>
          Dashboard
        </button>
        <span>›</span>
        <span>
          {periodLabel} : {formatPeriodLong(startDate, endDate)}
        </span>
        <span>›</span>
        <strong>{isPayslips ? "Payslips" : "Invoices"}</strong>
      </div>

      <div className="dash-period-card">
        <div className="flex justify-center mb-5">
          <div className="dash-main-tabs">
            <button
              type="button"
              className={isPayslips ? "is-active" : ""}
              onClick={() => onMainTab("payslips")}
            >
              Payslips
            </button>
            <button
              type="button"
              className={!isPayslips ? "is-active" : ""}
              onClick={() => onMainTab("invoices")}
            >
              Invoices
            </button>
          </div>
        </div>
        <h2 className="dash-pv-title">
          <Calendar size={20} color="#2E8056" strokeWidth={1.8} />
          <span>
            <span className="text-[#111827]">{periodLabel}:</span>{" "}
            <span className="text-primary">
              {formatPeriodLong(startDate, endDate)}
            </span>
          </span>
        </h2>

        {isPayslips ? (
          <div className="dash-stats-row dash-stats-row--detail">
            <div className="dash-stat-card">
              <DashIconWrap variant="dark">
                <PeopleIcon color={WHITE} />
              </DashIconWrap>
              <div className="dash-stat-copy">
                <p className="dash-stat-label">Total Employees</p>
                <p className="dash-stat-value">{totalUsers}</p>
              </div>
            </div>
            <div className="dash-stat-card">
              <DashIconWrap variant="dark">
                <InvoiceEditIcon color={WHITE} />
              </DashIconWrap>
              <div className="dash-stat-copy">
                <p className="dash-stat-label">Pending Payslips</p>
                <p className="dash-stat-value">{pendingCount}</p>
              </div>
            </div>
            <div className="dash-stat-card">
              <DashIconWrap variant="dark">
                <InvoiceDocIcon color={WHITE} />
              </DashIconWrap>
              <div className="dash-stat-copy">
                <p className="dash-stat-label">Generated Payslips</p>
                <p className="dash-stat-value">{generatedCount}</p>
              </div>
            </div>
            <div className="dash-stat-card">
              <DashIconWrap variant="dark">
                <ProgressChartIcon />
              </DashIconWrap>
              <div className="dash-stat-copy">
                <p className="dash-stat-label">Progress</p>
                <p className="dash-stat-value">{roundedPct}%</p>
              </div>
            </div>
            <div className="dash-progress-block">
              <div className="dash-progress-header">
                <p className="dash-progress-title">Payslip Progress</p>
                <span className="dash-progress-pct">
                  {roundedPct}% Complete
                </span>
              </div>
              <div className="dash-progress-track">
                <div
                  className="dash-progress-fill"
                  style={{ width: `${Math.max(0, Math.min(100, roundedPct))}%` }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="dash-stats-row dash-stats-row--detail">
            <div className="dash-stat-card">
              <DashIconWrap variant="dark">
                <DollarSignIcon color={WHITE} />
              </DashIconWrap>
              <div className="dash-stat-copy">
                <p className="dash-stat-label">Total Earning</p>
                <p className="dash-stat-value">
                  {formatDashMoney(stats.sentTotal)}
                </p>
              </div>
            </div>
            <div className="dash-stat-card">
              <DashIconWrap variant="dark">
                <InvoiceDocIcon color={WHITE} />
              </DashIconWrap>
              <div className="dash-stat-copy">
                <p className="dash-stat-label">Sent Invoice</p>
                <p className="dash-stat-value">{stats.sentCount} Invoices</p>
              </div>
            </div>
            <div className="dash-stat-card">
              <DashIconWrap variant="dark">
                <DollarHandIcon color={WHITE} />
              </DashIconWrap>
              <div className="dash-stat-copy">
                <p className="dash-stat-label">Draft Earning</p>
                <p className="dash-stat-value">
                  {formatDashMoney(stats.draftTotal)}
                </p>
              </div>
            </div>
            <div className="dash-stat-card">
              <DashIconWrap variant="dark">
                <InvoiceEditIcon color={WHITE} />
              </DashIconWrap>
              <div className="dash-stat-copy">
                <p className="dash-stat-label">Draft Invoice</p>
                <p className="dash-stat-value">{stats.draftCount} Invoices</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {isPayslips ? (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-[#E5E7EB] p-4 my-4">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none"
              />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                className="box-border w-full h-[42px] rounded-md border border-solid border-[#E5E7EB] pl-10 pr-3.5 text-[13px] font-normal text-[#1F2937] bg-white placeholder:text-[#94A3B8] outline-none"
              />
            </div>
          </div>

          <div className="flex flex-wrap justify-start gap-2 mb-4">
            {childTabs.map((tab) => {
              const active = childTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`dash-child-tab${active ? " is-active" : ""}`}
                  onClick={() => onChildTab(tab.id)}
                >
                  {tab.label}
                  <CountPill count={tab.count} active={active} />
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-[#E5E7EB] my-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 items-stretch sm:items-center">
            <div className="flex-1 min-w-0 relative flex items-center">
              <Search
                size={18}
                className="absolute left-4 text-[#94A3B8] pointer-events-none"
              />
              <input
                type="text"
                placeholder="Search by invoice #, client, or property"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                className="box-border w-full min-w-0 h-10 py-2 px-4 pl-12 rounded-lg border border-solid border-[#E5E7EB] text-sm outline-none transition-colors focus:border-[#2F7F57] bg-white text-[#1F2937] placeholder:text-[#9CA3AF]"
              />
            </div>
            <button
              type="button"
              className={`flex w-auto items-center justify-center py-2 px-4 sm:px-6 bg-[#2F7F57] text-white rounded-lg font-semibold text-sm no-underline transition-colors whitespace-nowrap hover:bg-[#256845] border-none cursor-pointer ${deniedActionClass(canCreateInvoice)}`}
              onClick={() => {
                guardAction(
                  PERMISSIONS.CREATE_INVOICE,
                  "generate invoice",
                  () => {
                    navigate("/generateInvoiceNew");
                  }
                );
              }}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Generate Invoice
            </button>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:gap-3 gap-3 items-stretch sm:items-center min-w-0">
              <SearchableSelect
                value={invoiceDateRange}
                onValueChange={setInvoiceDateRange}
                options={dateRangeOptions}
                labelPrefix="Date Range:"
                className={`w-full lg:min-w-[220px] ${FILTER_SELECT_CLASS}`}
              />
              <SearchableSelect
                value={invoiceProperty}
                onValueChange={setInvoiceProperty}
                options={propertyOptions}
                placeholder="All Properties"
                className={`w-full lg:min-w-[220px] ${FILTER_SELECT_CLASS}`}
              />
              <SearchableSelect
                value={invoiceStatus}
                onValueChange={setInvoiceStatus}
                options={statusOptions}
                placeholder="All Statuses"
                className={`w-full lg:min-w-[180px] ${FILTER_SELECT_CLASS}`}
              />
            </div>
            {hasInvoiceFilters ? (
              <button
                type="button"
                className="flex w-full lg:w-auto items-center justify-center py-2 lg:min-w-[160px] px-4 bg-transparent border-none text-[#1F2937] text-sm cursor-pointer rounded-lg transition-colors hover:bg-gray-100 whitespace-nowrap"
                onClick={() => {
                  setInvoiceDateRange("Current Period");
                  setInvoiceProperty("All Properties");
                  setInvoiceStatus("All");
                  onSearchChange("");
                  onSearchSubmit();
                }}
              >
                <MdFilterListOff className="w-4 h-4 mr-1" />
                Clear Filters
              </button>
            ) : null}
          </div>
        </div>
      )}

      {showPending && (
        <SectionCard
          title="Pending Payslips"
          count={pendingTotalSafe}
          icon={<PendingSectionIcon />}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse">
              <thead>
                <tr>
                  <th className={TH_CLASS}>Employee ID</th>
                  <th className={TH_CLASS}>Employee</th>
                  <th className={TH_CLASS}>Pay Period</th>
                  <th className={TH_CLASS}>Status</th>
                  <th className={TH_CLASS}>Amount</th>
                  <th className={TH_CLASS}>Last Updated</th>
                  <th className={TH_CLASS}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {userListLoading ? (
                  <tr>
                    <td className={TD_CLASS} colSpan="7">
                      <div className="flex justify-center py-6">
                        <TailSpin color="#2E8056" height={40} width={40} />
                      </div>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td className={`${TD_CLASS} text-center`} colSpan="7">
                      No pending payslips
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr
                      key={user.id}
                      className={`${TR_CLASS} cursor-pointer`}
                      onClick={() => openPendingRow(user)}
                    >
                      <td className={TD_CLASS}>
                        <Link
                          to="/userProfile"
                          state={user.id}
                          className="font-normal text-[#6B7280] no-underline"
                        >
                          #{user.id}
                        </Link>
                      </td>
                      <td className={TD_CLASS}>
                        <EmployeeCell
                          name={user.name}
                          email={user.email}
                          image={user.image}
                        />
                      </td>
                      <td className={TD_CLASS}>
                        {formatPeriodMid(startDate, endDate)}
                      </td>
                      <td className={TD_CLASS}>
                        <Badge variant="approved">Pending</Badge>
                      </td>
                      <td className={TD_CLASS}>—</td>
                      <td className={TD_CLASS}>
                        {formatLastUpdated(
                          user.updated_at || user.created_at || user.updatedAt
                        )}
                      </td>
                      <td className={TD_CLASS}>
                        <div className="flex items-center gap-1">
                          <ActionBtn
                            title="Generate Payslip"
                            className={deniedActionClass(canCreatePayslip)}
                            onClick={() => goGenerate(user.id)}
                          >
                            <ActionGenerateIcon />
                          </ActionBtn>
                          <ActionBtn
                            title="Generate Zero Invoice"
                            className={deniedActionClass(canCreatePayslip)}
                            onClick={() => onZeroSubmission(user.id)}
                          >
                            <ActionOmitIcon />
                          </ActionBtn>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <TablePager
            page={currentPage}
            pageSize={pageSize}
            total={pendingTotalSafe}
            onPageChange={onPendingPage}
          />
        </SectionCard>
      )}

      {showPrinted && (
        <SectionCard
          title="To Be Printed"
          count={printedTotalSafe}
          icon={<PrintedSectionIcon />}
        >
          <PayslipInvoiceTable
            loading={printedListLoading}
            rows={printedList}
            emptyText="No paylist generated yet"
            startDate={startDate}
            endDate={endDate}
            formatInvoiceAdditions={formatInvoiceAdditions}
            formatValue={formatValue}
            onRowClick={openPayslipRow}
            actions={(invoice) => (
              <div className="dash-actions">
                <ActionBtn
                  title="Archive Invoice"
                  className={deniedActionClass(canArchiveInvoice)}
                  onClick={() => archiveInvoice(invoice.id)}
                >
                  <ActionArchiveIcon />
                </ActionBtn>
                <ActionBtn
                  title="Edit Invoice"
                  className={deniedActionClass(canEditInvoice)}
                  onClick={() => {
                    guardAction(PERMISSIONS.EDIT, "edit invoice", () => {
                      navigate(editPayslipPath(invoice.id), {
                        state: invoice.id,
                      });
                    });
                  }}
                >
                  <ActionEditIcon />
                </ActionBtn>
                <ActionBtn
                  title="Delete Invoice"
                  className={deniedActionClass(canDeleteInvoice)}
                  onClick={() => handleDeleteInvoice(invoice.id)}
                >
                  <ActionDeleteIcon />
                </ActionBtn>
              </div>
            )}
            ytdCell={(invoice) => (
              <button
                type="button"
                className={`bg-transparent border-none p-0 text-sm font-medium text-[#111827] cursor-pointer ${deniedActionClass(canManageYtd)}`}
                onClick={() => onEditYtd(invoice)}
              >
                {payslipAmount(
                  invoice.YTDFinal || invoice.YTDAmount,
                  formatInvoiceAdditions
                )}
              </button>
            )}
          />
          <TablePager
            page={currentPagePrinted}
            pageSize={pageSizePrinted}
            total={printedTotalSafe}
            onPageChange={onPrintedPage}
          />
        </SectionCard>
      )}

      {showArchived && (
        <SectionCard
          title="Archived List"
          count={archivedTotal}
          icon={<ArchivedSectionIcon />}
          extra={
            <button
              type="button"
              className={`dash-gen-pdf ${deniedActionClass(canGeneratePdf)}`}
              onClick={(e) => {
                e.stopPropagation();
                handleGenerateAllPdfs();
              }}
            >
              Generate All PDFs
            </button>
          }
        >
          <PayslipInvoiceTable
            loading={archivedListLoading}
            rows={archivedList}
            emptyText="No archived payslip yet"
            startDate={startDate}
            endDate={endDate}
            formatInvoiceAdditions={formatInvoiceAdditions}
            formatValue={formatValue}
            onRowClick={openPayslipRow}
            actions={(invoice) => (
              <div className="dash-actions">
                <ActionBtn
                  title="Unarchive Invoice"
                  className={deniedActionClass(canArchiveInvoice)}
                  onClick={() => archiveInvoice(invoice.id, "unarchive")}
                >
                  <ActionUnarchiveIcon />
                </ActionBtn>
                <ActionBtn
                  title="Delete Invoice"
                  className={deniedActionClass(canDeleteInvoice)}
                  onClick={() => handleDeleteInvoice(invoice.id)}
                >
                  <ActionDeleteIcon />
                </ActionBtn>
              </div>
            )}
            ytdCell={(invoice) =>
              payslipAmount(
                invoice.YTDFinal || invoice.YTDAmount,
                formatInvoiceAdditions
              )
            }
          />
          <TablePager
            page={currentPageArchived}
            pageSize={pageSizeArchived}
            total={archivedTotal}
            onPageChange={onArchivedPage}
          />
        </SectionCard>
      )}

      {!isPayslips && (
        <div className="bg-white rounded-xl shadow-sm border border-[#E5E7EB] mb-4 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse">
              <thead>
                <tr>
                  <th className={TH_CLASS}>Invoice #</th>
                  <th className={TH_CLASS}>Property</th>
                  <th className={TH_CLASS}>Pay Period</th>
                  <th className={TH_CLASS}>Date</th>
                  <th className={TH_CLASS}>Total Amount</th>
                  <th className={TH_CLASS}>Status</th>
                  <th className={TH_CLASS}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoiceLoading ? (
                  <tr>
                    <td className={TD_CLASS} colSpan="7">
                      <div className="flex justify-center py-6">
                        <TailSpin color="#2E8056" height={40} width={40} />
                      </div>
                    </td>
                  </tr>
                ) : invoiceRows.length === 0 ? (
                  <tr>
                    <td className={`${TD_CLASS} text-center`} colSpan="7">
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  invoiceRows.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className={`${TR_CLASS} cursor-pointer`}
                      onClick={() => openCompanyInvoiceRow(invoice)}
                    >
                      <td className={TD_CLASS}>
                        <span className="font-normal text-[#6B7280]">
                          #{invoice.invoice_number}
                        </span>
                      </td>
                      <td className="p-4 text-sm font-medium text-[#111827] border-b border-[#E5E7EB] align-middle">
                        {invoice.property_name}
                      </td>
                      <td className={TD_CLASS}>
                        {formatPeriodMid(invoice.start_date, invoice.end_date)}
                      </td>
                      <td className={TD_CLASS}>
                        {invoice.invoice_date
                          ? moment(invoice.invoice_date).format("MMM D, YYYY")
                          : "—"}
                      </td>
                      <td className={TD_NUM_CLASS}>
                        ${Number(invoice.subtotal || invoice.total_amount || 0).toFixed(2)}
                      </td>
                      <td className={TD_CLASS}>
                        <Badge variant={getStatusVariant(invoice.status)}>
                          {invoice.status}
                        </Badge>
                      </td>
                      <td className={TD_CLASS}>
                        <div className="flex items-center gap-1">
                          <ActionBtn
                            title="View"
                            onClick={() => {
                              navigate(
                                `/generateInvoiceNew?invoiceId=${encodeURIComponent(
                                  String(invoice.id)
                                )}&forceStep=3`
                              );
                            }}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </ActionBtn>
                          <ActionBtn
                            title="Edit Invoice"
                            className={deniedActionClass(canEditInvoice)}
                            onClick={() => {
                              guardAction(PERMISSIONS.EDIT, "edit invoice", () => {
                                navigate(
                                  `/generateInvoiceNew?invoiceId=${encodeURIComponent(
                                    String(invoice.id)
                                  )}`
                                );
                              });
                            }}
                          >
                            <ActionEditIcon />
                          </ActionBtn>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <TablePager
            page={invoicePage}
            pageSize={invoicePageSize}
            total={invoiceTotal}
            onPageChange={setInvoicePage}
          />
        </div>
      )}
    </div>
  );
}

function PayslipInvoiceTable({
  loading,
  rows,
  emptyText,
  startDate,
  endDate,
  formatInvoiceAdditions,
  formatValue,
  actions,
  ytdCell,
  onRowClick,
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[860px] border-collapse">
        <thead>
          <tr>
            <th className={TH_CLASS}>Invoice ID</th>
            <th className={TH_CLASS}>Employee</th>
            <th className={TH_CLASS}>Pay Period</th>
            <th className={TH_CLASS}>Payrate</th>
            <th className={TH_CLASS}>Salary</th>
            <th className={TH_CLASS}>Hours</th>
            <th className={TH_CLASS}>Additions</th>
            <th className={TH_CLASS}>Deductions</th>
            <th className={TH_CLASS}>Amount</th>
            <th className={TH_CLASS}>YTD Amount</th>
            <th className={TH_CLASS}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td className={TD_CLASS} colSpan="11">
                <div className="flex justify-center py-6">
                  <TailSpin color="#2E8056" height={40} width={40} />
                </div>
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td className={`${TD_CLASS} text-center`} colSpan="11">
                {emptyText}
              </td>
            </tr>
          ) : (
            rows.map((invoice) => (
              <tr
                key={invoice.id}
                className={`${TR_CLASS}${onRowClick ? " cursor-pointer" : ""}`}
                onClick={() => onRowClick?.(invoice)}
              >
                <td className={TD_CLASS}>
                  <Link
                    to="/invoiceDetail"
                    state={invoice.id}
                    className="font-normal text-[#6B7280] no-underline"
                  >
                    #{invoice.id}
                  </Link>
                </td>
                <td className={TD_CLASS}>
                  <EmployeeCell
                    name={invoice.name}
                    email={invoice.email}
                    image={invoice.image}
                  />
                </td>
                <td className={TD_CLASS}>
                  {invoice.payPeriod || formatPeriodMid(startDate, endDate)}
                </td>
                <td className={TD_NUM_CLASS}>
                  {payslipAmount(invoice.payRate, formatInvoiceAdditions)}
                </td>
                <td className={TD_NUM_CLASS}>
                  {payslipAmount(invoice.salary, formatInvoiceAdditions)}
                </td>
                <td className={TD_NUM_CLASS}>
                  {formatValue(
                    invoice.hours +
                      ":" +
                      String(invoice.minutes || 0).padStart(2, "0")
                  )}
                </td>
                <td className={TD_NUM_CLASS}>
                  {payslipAmount(
                    invoice.Additions,
                    formatInvoiceAdditions,
                    formatValue
                  )}
                </td>
                <td className={TD_NUM_CLASS}>
                  {payslipAmount(
                    invoice.Deductions,
                    formatInvoiceAdditions,
                    formatValue
                  )}
                </td>
                <td className={TD_NUM_CLASS}>
                  {payslipAmount(invoice.Amount, formatInvoiceAdditions)}
                </td>
                <td className={TD_NUM_CLASS}>{ytdCell(invoice)}</td>
                <td className={TD_CLASS}>{actions(invoice)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default DashboardPeriodView;
